import { useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { Check, ChevronLeft, ChevronRight, X } from 'lucide-react'
import Button from '../forms/Button'
import type { PayorConfigurationFull } from '../../services/payorConfigurationService'
import {
  createPayor,
  getPayorConfiguration,
  savePayorConfigurationCappedRentalsStep,
  savePayorConfigurationFeeSchedulesStep,
  savePayorConfigurationOverviewStep,
  savePayorConfigurationOwnershipTransferStep,
  savePayorConfigurationPlansStep,
  savePayorConfigurationPriorAuthStep,
  finalizePayorConfiguration,
  uploadPayorPlansExcel,
} from '../../services/payorConfigurationService'
import { useToast } from '../../contexts/ToastContext'
import {
  PAYOR_CONFIG_TABS,
  type PayorConfigDraft,
  type PayorConfigTabId,
  type PayorOverviewErrors,
} from './payorConfigTypes'
import {
  createDraftFromPayor,
  emptyCappedRentals,
  validateOverview,
} from './payorConfigUtils'
import PayorOverviewTab from './tabs/PayorOverviewTab'
import PayorPlansPoliciesTab from './tabs/PayorPlansPoliciesTab'
import PayorFeeSchedulesTab from './tabs/PayorFeeSchedulesTab'
import PayorCappedRentalsTab from './tabs/PayorCappedRentalsTab'
import PayorOwnershipTransferTab from './tabs/PayorOwnershipTransferTab'
import PayorPriorAuthTab from './tabs/PayorPriorAuthTab'

type PayorConfigurationDrawerProps = {
  open: boolean
  mode: 'create' | 'edit'
  configurationId: string | null
  subtitle?: string
  onClose: () => void
  onComplete: () => void
}

const PayorConfigurationDrawer = ({
  open,
  mode,
  configurationId: initialConfigurationId,
  subtitle: subtitleOverride,
  onClose,
  onComplete,
}: PayorConfigurationDrawerProps) => {
  const [configurationId, setConfigurationId] = useState<string | null>(initialConfigurationId)
  const [activeTab, setActiveTab] = useState<PayorConfigTabId>('overview')
  const [draft, setDraft] = useState<PayorConfigDraft>(() => createDraftFromPayor(null))
  const [overviewErrors, setOverviewErrors] = useState<PayorOverviewErrors>({})
  const [isLoadingConfig, setIsLoadingConfig] = useState(false)
  const [isSavingStep, setIsSavingStep] = useState(false)
  const { showToast } = useToast()

  useEffect(() => {
    if (!open) return

    setConfigurationId(initialConfigurationId)
    setOverviewErrors({})

    if (!initialConfigurationId) {
      setDraft(createDraftFromPayor(null))
      setActiveTab('overview')
      return
    }

    let cancelled = false
    setIsLoadingConfig(true)

    void getPayorConfiguration(initialConfigurationId)
      .then((config: PayorConfigurationFull) => {
        if (cancelled) return
        setDraft(createDraftFromPayor(config))
        setActiveTab('overview')
      })
      .catch((error: unknown) => {
        if (cancelled) return
        showToast(
          error instanceof Error ? error.message : 'Unable to load payor configuration.',
          { type: 'error' }
        )
        setDraft(createDraftFromPayor(null))
        setActiveTab('overview')
      })
      .finally(() => {
        if (!cancelled) setIsLoadingConfig(false)
      })

    return () => {
      cancelled = true
    }
  }, [open, initialConfigurationId, showToast])

  useEffect(() => {
    if (!open) return
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = previousOverflow
    }
  }, [open])

  const close = () => {
    setActiveTab('overview')
    setOverviewErrors({})
    onClose()
  }

  const activeTabIndex = useMemo(
    () => PAYOR_CONFIG_TABS.findIndex((tab) => tab.id === activeTab),
    [activeTab]
  )
  const isFirstTab = activeTabIndex <= 0
  const isLastTab = activeTabIndex === PAYOR_CONFIG_TABS.length - 1
  const totalSteps = PAYOR_CONFIG_TABS.length

  const validateCurrentTab = (): boolean => {
    if (activeTab === 'overview') {
      const errors = validateOverview(draft.overview)
      if (Object.keys(errors).length > 0) {
        setOverviewErrors(errors)
        showToast('Please complete the required payor details.', { type: 'error' })
        return false
      }
      return true
    }

    if (activeTab === 'plans') {
      const incompletePlan = draft.plans.find((plan) => !plan.planName.trim())
      if (incompletePlan) {
        showToast('Please complete or remove the draft plan before proceeding.', {
          type: 'error',
        })
        return false
      }
      return true
    }

    if (activeTab === 'fee-schedules') {
      const incompleteRow = draft.feeSchedules.find(
        (row) => row.isDraft && !row.hcpcsCode.trim()
      )
      if (incompleteRow) {
        showToast(
          'Please complete or remove the draft fee schedule row before proceeding.',
          { type: 'error' }
        )
        return false
      }
      return true
    }

    return true
  }

  const ensurePayorId = (): string => {
    if (!configurationId) {
      throw new Error('Complete and save payor details (step 1) before continuing.')
    }
    return configurationId
  }

  const createPayorStatus = (overview: PayorConfigDraft['overview']): string => {
    if (!overview.activePayor || overview.status === 'inactive') return 'inactive'
    return 'active'
  }

  const saveCurrentStep = async (): Promise<{ nextStep: PayorConfigTabId | null }> => {
    if (activeTab === 'overview') {
      let payorId = configurationId
      const isFirstCreate = mode === 'create' && !payorId
      if (isFirstCreate) {
        payorId = await createPayor({
          name: draft.overview.payorName.trim(),
          status: createPayorStatus(draft.overview),
        })
        setConfigurationId(payorId)
      }
      if (!payorId) {
        throw new Error('Payor record is missing. Unable to save overview.')
      }
      const stepResult = await savePayorConfigurationOverviewStep(
        payorId,
        draft.overview,
        { isUpdate: mode === 'edit' || !isFirstCreate }
      )
      setConfigurationId(stepResult.payorId)
      return { nextStep: stepResult.nextStep }
    }

    const payorId = ensurePayorId()

    if (activeTab === 'plans') {
      const stepResult = await savePayorConfigurationPlansStep(
        payorId,
        draft.plans,
        draft.policyRules
      )
      return { nextStep: stepResult.nextStep }
    }

    if (activeTab === 'fee-schedules') {
      const stepResult = await savePayorConfigurationFeeSchedulesStep(
        payorId,
        draft.feeScheduleMeta.scheduleYear,
        draft.feeScheduleMeta.scheduleLabel,
        draft.feeSchedules
      )
      return { nextStep: stepResult.nextStep }
    }

    if (activeTab === 'capped-rentals') {
      const stepResult = await savePayorConfigurationCappedRentalsStep(
        payorId,
        draft.cappedRentals ?? emptyCappedRentals()
      )
      return { nextStep: stepResult.nextStep }
    }

    if (activeTab === 'ownership-transfer') {
      const stepResult = await savePayorConfigurationOwnershipTransferStep(
        payorId,
        draft.ownershipTransfer
      )
      return { nextStep: stepResult.nextStep }
    }

    throw new Error('Unsupported wizard step.')
  }

  const advanceAfterStep = (nextStep: PayorConfigTabId | null) => {
    if (nextStep) {
      setActiveTab(nextStep)
      return
    }
    if (!isLastTab) {
      setActiveTab(PAYOR_CONFIG_TABS[activeTabIndex + 1].id)
    }
  }

  const handleSaveAndProceed = async () => {
    if (!validateCurrentTab()) return

    setIsSavingStep(true)
    try {
      const { nextStep } = await saveCurrentStep()
      advanceAfterStep(nextStep)
      showToast('Step saved.', { type: 'success' })
    } catch (error) {
      showToast(
        error instanceof Error ? error.message : 'Failed to save this wizard step.',
        { type: 'error' }
      )
    } finally {
      setIsSavingStep(false)
    }
  }

  const handleFinish = async () => {
    if (!validateCurrentTab()) return

    let payorId: string
    try {
      payorId = ensurePayorId()
    } catch (error) {
      showToast(error instanceof Error ? error.message : 'Payor record is not ready.', {
        type: 'error',
      })
      setActiveTab('overview')
      return
    }

    setIsSavingStep(true)
    try {
      await savePayorConfigurationPriorAuthStep(payorId, draft.priorAuth)
      await finalizePayorConfiguration(payorId)
      showToast(
        mode === 'edit' ? 'Payor configuration updated.' : 'Payor configuration completed.',
        { type: 'success' }
      )
      onComplete()
      close()
    } catch (error) {
      showToast(
        error instanceof Error ? error.message : 'Failed to save prior auth settings.',
        { type: 'error' }
      )
    } finally {
      setIsSavingStep(false)
    }
  }

  const handlePlansExcelUpload = async (file: File) => {
    if (!configurationId) {
      showToast('Save payor details before uploading plans.', { type: 'error' })
      return
    }
    try {
      const imported = await uploadPayorPlansExcel(configurationId, file)
      setDraft((prev) => ({
        ...prev,
        plans: [...prev.plans.filter((plan) => plan.planName.trim()), ...imported],
      }))
      showToast(
        imported.length > 0
          ? `Imported ${imported.length} plan${imported.length === 1 ? '' : 's'} from spreadsheet.`
          : 'Upload completed. Review plans before saving this step.',
        { type: 'success' }
      )
    } catch (error) {
      showToast(
        error instanceof Error ? error.message : 'Failed to upload plans spreadsheet.',
        { type: 'error' }
      )
      throw error
    }
  }

  const goToPreviousTab = () => {
    if (isFirstTab) return
    setActiveTab(PAYOR_CONFIG_TABS[activeTabIndex - 1].id)
  }

  const updateOverview = (
    field: keyof PayorConfigDraft['overview'],
    value: string | boolean
  ) => {
    setDraft((prev) => {
      const nextOverview = { ...prev.overview, [field]: value }
      if (field === 'activePayor') {
        nextOverview.status = value ? 'active' : 'inactive'
      }
      return { ...prev, overview: nextOverview }
    })
    if (overviewErrors[field as keyof PayorOverviewErrors]) {
      setOverviewErrors((prev) => ({ ...prev, [field]: undefined }))
    }
  }

  const title = mode === 'edit' ? 'Edit payor' : 'Add payor'
  const subtitle =
    subtitleOverride ??
    (mode === 'edit'
      ? draft.overview.payorName || 'Configure payor details, plans, and billing rules'
      : 'Configure payor details, plans, and billing rules')

  if (!open) return null

  return createPortal(
    <>
      <div
        className="fixed inset-0 top-0 bg-slate-900/35 z-[200] transition-opacity opacity-100 pointer-events-auto"
        onClick={close}
        aria-hidden={false}
      />
      <div
        className="fixed top-0 right-0 bottom-0 w-full sm:w-3/4 bg-white border-l border-gray-200 z-[201] flex flex-col shadow-[-8px_0_32px_rgba(15,23,42,0.1)] transition-transform duration-300 translate-x-0"
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        <div className="flex items-center justify-between px-6 sm:px-8 py-5 border-b border-gray-200 shrink-0">
          <div className="min-w-0">
            <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
            <p className="text-sm text-gray-500 mt-1 truncate">{subtitle}</p>
          </div>
          <button
            type="button"
            onClick={close}
            className="w-8 h-8 rounded-lg border border-gray-200 flex items-center justify-center text-gray-500 hover:bg-gray-100 shrink-0"
            aria-label="Close"
          >
            <X size={16} />
          </button>
        </div>

        <div className="border-b border-gray-200 bg-white shrink-0 px-6 sm:px-8 py-4">
          <div className="overflow-x-auto">
            <div className="flex min-w-max items-center gap-2 sm:gap-3">
              {PAYOR_CONFIG_TABS.map((tab, index) => {
                const stepNumber = index + 1
                const isCompleted = index < activeTabIndex
                const isCurrent = activeTab === tab.id
                const stepContent = (
                  <>
                    <span
                      className={`inline-flex h-6 w-6 items-center justify-center rounded-full text-[11px] font-semibold ${
                        isCompleted
                          ? 'bg-green-500 text-white'
                          : isCurrent
                            ? 'bg-primary-600 text-white'
                            : 'bg-gray-200 text-gray-600'
                      }`}
                    >
                      {isCompleted ? <Check size={12} /> : stepNumber}
                    </span>
                    <span
                      className={`text-xs sm:text-sm font-medium whitespace-nowrap ${
                        isCurrent ? 'text-gray-900' : 'text-gray-500'
                      }`}
                    >
                      {tab.label}
                    </span>
                  </>
                )
                return (
                  <div key={tab.id} className="flex items-center gap-2 sm:gap-3">
                    {mode === 'edit' ? (
                      <button
                        type="button"
                        onClick={() => setActiveTab(tab.id)}
                        disabled={isLoadingConfig}
                        className={`inline-flex items-center gap-2 rounded-full transition-colors ${
                          isCurrent ? '' : 'hover:opacity-80'
                        } disabled:opacity-50 disabled:cursor-not-allowed`}
                        aria-current={isCurrent ? 'step' : undefined}
                      >
                        {stepContent}
                      </button>
                    ) : (
                      <div
                        className="inline-flex items-center gap-2 rounded-full"
                        aria-current={isCurrent ? 'step' : undefined}
                      >
                        {stepContent}
                      </div>
                    )}
                    {index < totalSteps - 1 && <span className="h-px w-5 sm:w-7 bg-gray-300" />}
                  </div>
                )
              })}
            </div>
          </div>
          <p className="mt-3 text-xs text-gray-500">
            Step {activeTabIndex + 1} of {totalSteps}
          </p>
        </div>

        <div className="flex-1 overflow-y-auto px-6 sm:px-8 py-6 bg-gray-50/40">
          {isLoadingConfig ? (
            <p className="text-sm text-gray-500">Loading payor configuration…</p>
          ) : (
            <>
              {activeTab === 'overview' && (
                <PayorOverviewTab
                  overview={draft.overview}
                  errors={overviewErrors}
                  onChange={updateOverview}
                />
              )}
              {activeTab === 'plans' && (
                <PayorPlansPoliciesTab
                  plans={draft.plans}
                  policyRules={draft.policyRules}
                  onPlansChange={(plans) => setDraft((prev) => ({ ...prev, plans }))}
                  onPolicyRulesChange={(policyRules) =>
                    setDraft((prev) => ({ ...prev, policyRules }))
                  }
                  onUploadExcel={configurationId ? handlePlansExcelUpload : undefined}
                />
              )}
              {activeTab === 'fee-schedules' && (
                <PayorFeeSchedulesTab
                  scheduleMeta={draft.feeScheduleMeta}
                  rows={draft.feeSchedules}
                  onScheduleMetaChange={(feeScheduleMeta) =>
                    setDraft((prev) => ({ ...prev, feeScheduleMeta }))
                  }
                  onChange={(feeSchedules) => setDraft((prev) => ({ ...prev, feeSchedules }))}
                />
              )}
              {activeTab === 'capped-rentals' && (
                <PayorCappedRentalsTab
                  form={draft.cappedRentals ?? emptyCappedRentals()}
                  onChange={(cappedRentals) => setDraft((prev) => ({ ...prev, cappedRentals }))}
                />
              )}
              {activeTab === 'ownership-transfer' && (
                <PayorOwnershipTransferTab
                  form={draft.ownershipTransfer}
                  onChange={(ownershipTransfer) =>
                    setDraft((prev) => ({ ...prev, ownershipTransfer }))
                  }
                />
              )}
              {activeTab === 'prior-auth' && (
                <PayorPriorAuthTab
                  form={draft.priorAuth}
                  onChange={(priorAuth) => setDraft((prev) => ({ ...prev, priorAuth }))}
                />
              )}
            </>
          )}
        </div>

        <div className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-3 px-6 sm:px-8 py-4 border-t border-gray-200 bg-white shrink-0">
          <div className="flex items-center gap-3">
            <Button type="button" variant="outline" onClick={close} disabled={isSavingStep}>
              Cancel
            </Button>
            {!isFirstTab && (
              <Button
                type="button"
                variant="secondary"
                onClick={goToPreviousTab}
                disabled={isSavingStep || isLoadingConfig}
                className="inline-flex items-center gap-1.5"
              >
                <ChevronLeft size={16} />
                Previous
              </Button>
            )}
          </div>
          <div className="flex items-center gap-3 sm:justify-end">
            {!isLastTab ? (
              <Button
                type="button"
                variant="primary"
                onClick={() => void handleSaveAndProceed()}
                disabled={isSavingStep || isLoadingConfig}
                className="inline-flex items-center gap-1.5"
              >
                {isSavingStep ? 'Saving…' : 'Save and proceed'}
                <ChevronRight size={16} />
              </Button>
            ) : (
              <Button
                type="button"
                variant="primary"
                onClick={() => void handleFinish()}
                disabled={isSavingStep || isLoadingConfig}
              >
                {isSavingStep
                  ? 'Saving…'
                  : mode === 'edit'
                    ? 'Save changes'
                    : 'Complete payor'}
              </Button>
            )}
          </div>
        </div>
      </div>
    </>,
    document.body
  )
}

export default PayorConfigurationDrawer
